/**
 * Money is an integer count of minor units (paise, cents) plus an ISO 4217 currency code, exactly as the
 * backend sends it. This module only validates and formats. It has no arithmetic on purpose: the
 * frontend never calculates a total, a tax, a discount or a balance (PFD-02). Show what the server sent.
 */

/** Integer minor units, for example `107100` for 1,071.00 INR. */
export type MinorUnits = number;

export interface Money {
  amount: MinorUnits;
  /** ISO 4217 code, for example `INR`. Comes from the branch (`branch_currency`). */
  currency: string;
}

export const DEFAULT_LOCALE = 'en-IN';

/** Returns the value when it is a safe integer; throws otherwise (a float here means a contract bug). */
export function assertMinorUnits(value: unknown, label = 'amount'): MinorUnits {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
    throw new TypeError(
      `${label} must be an integer number of minor units (received ${String(value)})`,
    );
  }
  return value;
}

export function money(amount: unknown, currency: string): Money {
  return { amount: assertMinorUnits(amount), currency };
}

/** Number of minor-unit digits for a currency (2 for INR, 0 for JPY, 3 for KWD), from the platform's data. */
export function currencyFractionDigits(currency: string, locale: string = DEFAULT_LOCALE): number {
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** Exact decimal string for a minor-unit amount, built with integer arithmetic only. */
function toDecimalString(amount: MinorUnits, fractionDigits: number): `${number}` {
  const negative = amount < 0;
  const abs = BigInt(Math.abs(amount));
  const base = 10n ** BigInt(fractionDigits);
  const whole = abs / base;
  const fraction = abs % base;
  const text =
    fractionDigits === 0
      ? `${whole}`
      : `${whole}.${fraction.toString().padStart(fractionDigits, '0')}`;
  return (negative ? `-${text}` : text) as `${number}`;
}

/** Presentation only: "₹1,071.00". Never feeds a calculation. */
export function formatMoney(value: Money, locale: string = DEFAULT_LOCALE): string {
  const amount = assertMinorUnits(value.amount);
  const digits = currencyFractionDigits(value.currency, locale);
  const decimal = toDecimalString(amount, digits);
  try {
    // A decimal string keeps full precision; a float would not.
    return new Intl.NumberFormat(locale, { style: 'currency', currency: value.currency }).format(
      decimal,
    );
  } catch {
    return `${decimal} ${value.currency}`;
  }
}
